import React, { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { PageHeader, TabHeaderItem, TabsHeader, showYesNo, showConfirmation } from '@modules/common';
import { Container, Badge } from 'reactstrap';
import { ProposalsPageBloc } from './ProposalsPageBloc';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { useBloc } from '@core/utils/bloc';
import { TopLoadingBar } from '@core/components/LoadingBar';
// import { openAddProposalModal } from '../AddProposalModal';
import { CBody } from '@modules/common/CBody';
import { GCTable, GCColumn } from '@core/components/GCTable';
import { CompactProposalTableResponse, ProposalDiscountType, ProposalId, ProposalStatus } from '@business/entities/proposal';
import { R } from '@core/utils/r';
import { openViewProposal } from '../ViewProposalModal';
import { useHistory, useRouteParam } from '@core/router';
import { ProposalService } from '@business/services/proposal_service';
import { MoreMenu } from '@modules/common/MoreMenu';
import { openResendProposal } from '../ViewProposalModal/ResendProposalModal';
import moment from 'moment';
import { toMoment } from '@core/utils/time';
import { Formatter } from '@core/utils/formatter';
import { CommonService } from '@business/services';
import { FormSelect, FormInput } from '@core/components/form';
import { formControl, FormDataType, formGroup } from '@core/utils/form';
import { UserId } from '@business/entities/account';
import { t } from 'ttag';
import { switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { openGenericInputModal } from '@modules/common/GenericInputModal';
import { DateRangeTypes } from '@business/entities/dynamicReprots';
import { FormDateRangeExtra } from '@core/components/form/FormDateRangeExtra';
import { classnames } from '@core/utils/css';
import { openEmailTemplateModal } from '@modules/common/EmailTemplateModal';
import { ContactAPI } from '@business/api/contact';
import { useCompany } from '@core/utils/hooks/misc';
import { FiltersData } from '@modules/ClientPortal/ClientProposal/ClientProposalPage';
import { E } from '@fullcalendar/resource/internal-common';

export function getDateRangeValue(defaultRange: DateRangeTypes) {
  switch (defaultRange) {
    case DateRangeTypes.today:
      return `${toMoment().startOf('day').unix()}-${toMoment().endOf('day').unix()}`;
    case DateRangeTypes.yesterday:
      return `${toMoment().subtract(1, 'day').startOf('day').unix()}-${toMoment().subtract(1, 'day').endOf('day').unix()}`;
    case DateRangeTypes.last7:
      return `${toMoment().subtract(7, 'day').unix()}-${toMoment().unix()}`;
    case DateRangeTypes.last30:
      return `${toMoment().subtract(30, 'day').unix()}-${toMoment().unix()}`;
    case DateRangeTypes.month:
      return `${toMoment().startOf('month').unix()}-${toMoment().endOf('month').unix()}`;
    case DateRangeTypes.lastMonth:
      return `${toMoment().subtract(1, 'month').startOf('month').unix()}-${toMoment().subtract(1, 'month').endOf('month').unix()}`;
    case DateRangeTypes.year:
      return `${toMoment().startOf('year').unix()}-${toMoment().endOf('year').unix()}`;
    case DateRangeTypes.lastYear:
      return `${toMoment().subtract(1, 'year').startOf('year').unix()}-${toMoment().subtract(1, 'year').endOf('year').unix()}`;
  }
}

function defineFilterForm() {
  return formGroup({
    createdBy: formControl<UserId>({
      placeholder: t`Select User`,
    }),
    search: formControl<string>({
      placeholder: t`Search`,
    }),
    dateRange: formControl<string>({
      initialValue: getDateRangeValue(DateRangeTypes.year),
    }),
  });
}
export type ProposalFilterData = FormDataType<typeof defineFilterForm>;

export interface ProposalsPageProps {
  proposalId?: ProposalId;
  filters?: FiltersData;
}

export const ProposalsPage = memo((props: ProposalsPageProps) => {
  const { proposalId, filters = null } = props;
  const paramProposalId = useRouteParam('id', true);
  const paramStatus = useRouteParam('status', true) as ProposalStatus;
  const viewTab = useRouteParam('tab', true) ?? null;
  const bloc = useBloc(ProposalsPageBloc);
  const loading = useLoadingState(bloc.loading);
  const proposals = useObservable(bloc.proposals$, []) as CompactProposalTableResponse[];
  const form = useMemo(() => defineFilterForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const [status, setStatus] = useState<ProposalStatus | null>(null);
  const tabs: TabHeaderItem[] = useMemo(() => {
    if (filters?.tab) {
      return filters.tab;
    } else {
      return [{ title: 'All' }, { title: 'Drafts' }, { title: 'Sent' }, { title: 'Viewed' }, { title: 'Signed' }, { title: 'Archived' }];
    }
  }, [filters]);
  const company = useCompany();
  const history = useHistory();
  const users = useObservable(bloc.users$, []);
  const userItems = useMemo(() => CommonService.selectItems(users, u => CommonService.getFullName(u), 'id', true), [users]);
  const [currentProposalId, setCurrentProposalId] = useState<ProposalId | null>(null);
  const [currentProposalStatus, setCurrentProposalStatus] = useState<ProposalStatus | null>(null);

  const [init, setInit] = useState(false);
  const [datesEnabled, setDatesEnabled] = useState(false);

  const [initProp, setInitProp] = useState(false);

  useEffect(() => {
    if (!paramProposalId) {
      setInitProp(false);
      setInit(false);
    }
  }, [paramProposalId]);

  useEffect(() => {
    if (proposalId && !init && proposals.length > 0) {
      openViewProposal({ proposalId, viewTab });
      setInit(true);
    }
  }, [proposalId, init, proposals.length, viewTab]);

  useEffect(() => {
    bloc.onDebouncedSearch(formValue.search || '');
  }, [bloc, formValue.search, init]);

  useEffect(() => {
    if (paramStatus != currentProposalStatus && paramProposalId != currentProposalId) {
      setCurrentProposalStatus(paramStatus);
    } else if (paramProposalId != currentProposalId) {
      setCurrentProposalId(paramProposalId);
    }
  }, [currentProposalId, currentProposalStatus, paramProposalId, paramStatus]);

  useEffect(() => {
    if (!initProp && currentProposalStatus && currentProposalId && proposals.length > 0) {
      const data = R.find(proposals, o => o.id == currentProposalId);
      if (data?.status == ProposalStatus.draft) {
        // openAddProposalModal({ proposalId: currentProposalId, orgId: data?.orgId || '0' });
      } else openViewProposal({ proposalId: currentProposalId, viewTab });
      setInitProp(true);
    } else if (!initProp && currentProposalId && proposals.length > 0 && paramProposalId) {
      openViewProposal({ proposalId: currentProposalId, viewTab });
      setInitProp(true);
    }
  }, [currentProposalId, proposals.length, currentProposalStatus, proposals, initProp, paramProposalId, viewTab]);

  const onViewProposal = useCallback(
    (id: string) => {
      history.push(`/proposals/${id}`);
    },
    [history],
  );

  const onProposalClick = useCallback(
    (row: CompactProposalTableResponse) => {
      if (row.status === ProposalStatus.draft) {
        // onAddProposalModal(row.id);
        history.push(`/add-edit-proposal/${row.estimateId}/${row.id}?lastUrl=/proposals`);
      } else {
        onViewProposal(row.id);
      }
    },
    [onViewProposal],
  );

  const showProducts = useCallback(
    (row: CompactProposalTableResponse) => {
      return history.push(`/proposals/${row.id}/products/view`);
    },
    [history],
  );

  const onEditProposalTitle = useCallback(
    (proposalId: ProposalId, title: string) => {
      openGenericInputModal({
        title: 'Update Title',
        label: 'Title',
        button: 'Update',
        initialValue: title,
        onSubmit: name => bloc.updateTitle(proposalId, name),
      });
    },
    [bloc],
  );

  const columns: GCColumn<CompactProposalTableResponse>[] = useMemo(
    () => [
      {
        name: 'Title',
        sortField: 'title',
        minWidth: '500px',
        cell: row => {
          return (
            <div className="d-flex flex-row mt-2 mb-2 align-items-center" onClick={() => onProposalClick(row)}>
              <div style={{ position: 'relative' }}>
                <img
                  style={{
                    width: 80,
                    height: 105,
                    objectFit: 'cover',
                    objectPosition: 'top',
                    borderRadius: 10,
                  }}
                  src={row.image}
                />
                <Badge
                  className="d-flex flex-row p-1 align-items-center text-center"
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    right: 0,
                    fontSize: 12,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomLeftRadius: 10,
                    borderBottomRightRadius: 10,
                    justifyContent: 'center',
                  }}
                  color={row.isExpired && !row.deletedAt ? 'danger' : ProposalService.getStatusColor(row.status, !!row.deletedAt)}
                >
                  {row.deletedAt ? 'Archived' : row.isExpired ? 'Expired' : R.upperFirst(row.status)}
                </Badge>
              </div>
              <div className="d-flex flex-column ml-4">
                <span className={`text-dark font-weight-medium font-size-16`}>{row.title}</span>
                <span className="text-muted font-size-12">Created By: {CommonService.getFullName(row.createdBy)}</span>
                {row.signer ? (
                  <span className="text-muted font-size-12">
                    Sent: {toMoment(row.created).format('MMM, DD YYYY')} - Signed: {moment(row.signer.signed_at).format('MMM, DD [at] YYYY hh:mm A')}
                  </span>
                ) : (
                  <span className="text-muted font-size-12">Sent: {toMoment(row.created).format('MMM, DD YYYY')}</span>
                )}
                {row.depositAt && (
                  <span className="text-muted font-size-12">
                    Deposited: {Formatter.formatCurrency(row.deposit, company.currencyCode)} on {toMoment(row.depositAt).format('MMM, DD YYYY')}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        name: 'history',
        sortField: 'history',
        minWidth: '400px',
        cell: row => {
          return (
            <div onClick={() => onProposalClick(row)}>
              {R.take(row.history, 2).map(item => (
                <div key={item.id} className="d-flex flex-row mb-1 text-muted">
                  <div className="mr-2  font-size-12" style={{ width: 60 }}>
                    {Formatter.formatTimeRelative(item.createdAt)}
                  </div>
                  <div style={{ flex: 1, fontWeight: 500, fontSize: 12 }}>{item.message}</div>
                </div>
              ))}
            </div>
          );
        },
      },

      {
        name: 'Status',
        sortField: 'status',
        width: '400px',
        cell: row => (
          <div className="d-flex flex-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', width: 300 }}>
            <div className="d-flex flex-column" style={{ justifyContent: 'flex-end', alignItems: 'center', width: 300 }}>
              {/* {row.products
                .filter(p => !p.isOptional || (p.selected && p.isOptional))
                .map((item, index) => {
                  return (
                    <div key={index} className="d-flex flex-row" style={{ justifyContent: 'center', alignItems: 'flex-start', width: 300 }}>
                      <div style={{ flex: 1, fontWeight: 400, fontSize: 12, width: 220 }} className="text-muted">
                        {item.title}
                      </div>
                      <div style={{ flex: 1, fontWeight: 500, fontSize: 12, width: 80 }} className="text-muted">
                        {item.price}
                      </div>
                    </div>
                  );
                })} */}
              <div className="d-flex flex-row">
                {/* show more button */}
                <a onClick={() => showProducts(row)} style={{ cursor: 'pointer' }}>
                  Show Products
                </a>
              </div>
              <div className="d-flex flex-row" style={{ justifyContent: 'center', alignItems: 'flex-start', width: 300, marginTop: 5 }}>
                <div style={{ flex: 1, fontWeight: 400, fontSize: 12, width: 220 }} className="text-muted" />
                <div style={{ flex: 1, width: 80 }}>
                  <span className={`text-dark font-weight-medium font-size-16`}>{Formatter.formatCurrency(row.amount, company.currencyCode)}</span>
                </div>
              </div>
            </div>
            <div style={{ alignItems: 'center', flexDirection: 'row', display: 'flex' }}>
              <div className="d-flex">
                <MoreMenu
                  className="float-right"
                  vertical
                  items={[
                    {
                      title: 'Change Name',
                      icon: 'fa fa-edit',
                      onClick: () => onEditProposalTitle(row.id, row.title),
                    },
                    {
                      icon: 'fa fa-copy',
                      title: 'Duplicate Proposal',
                      // onClick: () => openAddProposalModal({ proposalId: row.id, orgId: row.orgId, duplicate: true }),
                    },
                    {
                      icon: 'fa fa-envelope',
                      title: 'Resend Proposal',
                      onClick: () => openResendProposal({ proposalId: row.id, orgId: row.orgId }),
                    },
                    {
                      icon: 'fa fa-archive',
                      className: 'text-danger',
                      title: !row.deletedAt ? 'Archive' : 'Unarchive',
                      //   visible: !row.deletedAt,
                      onClick: () => {
                        if (row.deletedAt) {
                          of(null)
                            .pipe(
                              switchMap(() => showYesNo('Unarchive Proposal', `Do you really want to unarchive this proposal?`)),
                              tap(res => {
                                if (res) {
                                  bloc
                                    .unarchiveProposal(row.id)
                                    .pipe(tap(() => bloc.onReload().subscribe()))
                                    .subscribe();
                                }
                              }),
                            )
                            .subscribe();
                        } else {
                          bloc
                            .deleteProposal(row.id)
                            .pipe(
                              tap(() => bloc.onReload().subscribe()),
                              switchMap(() =>
                                showConfirmation('Send Email', `Do you want to send email to the client?`, { btnYesLabel: 'Send Email' }),
                              ),
                              switchMap(() => {
                                return openEmailTemplateModal({
                                  emails: row.senderEmails,
                                  messageRequired: true,
                                  subjectRequired: true,
                                }).pipe(
                                  switchMap(data => {
                                    return ContactAPI.sendGenericEmail(data);
                                  }),
                                );
                              }),
                            )
                            .subscribe();
                        }
                      },
                    },
                    // {
                    //   icon: 'bx bx-minus-circle',
                    //   title: 'Void Proposal',
                    //   onClick: () => {
                    //     bloc.updateStatus(row.id, { status: ProposalStatus.void }).subscribe();
                    //   },
                    // },
                    // {
                    //   icon: 'bx bx-x',
                    //   title: 'Cancel Proposal',
                    //   onClick: () => {
                    //     bloc.updateStatus(row.id, { status: ProposalStatus.cancel }).subscribe();
                    //   },
                    // },
                  ]}
                />
              </div>
            </div>
          </div>
        ),
      },
    ],
    [bloc, company, onEditProposalTitle, onProposalClick, showProducts],
  );

  const onTabIndexChange = useCallback((index: number) => {
    if (!index) return setStatus(null);
    if (index === 1) return setStatus(ProposalStatus.draft);
    if (index === 2) return setStatus(ProposalStatus.sent);
    if (index === 3) return setStatus(ProposalStatus.viewed);
    if (index === 4) return setStatus(ProposalStatus.accepted);
    if (index === 5) return setStatus(ProposalStatus.archived);
    // if (index === 6) return setStatus(ProposalStatus.void);
    // if (index === 7) return setStatus(ProposalStatus.cancel);
  }, []);

  const items = useMemo(() => {
    const createdBy = formValue.createdBy;
    if (createdBy) {
      const filtered =
        status && status == ProposalStatus.archived
          ? proposals.filter(p => p.deletedAt && p.createdBy.id == createdBy)
          : !status
            ? proposals.filter(p => !p.deletedAt && p.createdBy.id == createdBy)
            : proposals.filter(p => (!status || (p.status === status && !p.deletedAt)) && p.createdBy.id == createdBy);
      return R.sortBy(filtered, u => -u.created);
    } else {
      const filtered =
        status && status == ProposalStatus.archived
          ? proposals.filter(p => p.deletedAt)
          : !status
            ? proposals.filter(p => !p.deletedAt)
            : proposals.filter(p => !status || (p.status === status && !p.deletedAt));
      return R.sortBy(filtered, u => -u.created);
    }
  }, [proposals, status, formValue.createdBy]);

  const itemsByDateRange = useMemo(() => {
    if (!datesEnabled) return items;
    const dateRange = formValue.dateRange;
    const [startDate, endDate] = dateRange.split('-');
    const start = R.toNumber(startDate);
    const end = R.toNumber(endDate);
    return items.filter(item => {
      if (!status) {
        return (
          (item.created >= start && item.created <= end) ||
          (item.signedAt && item.signedAt >= start && item.signedAt <= end) ||
          (item.viewedAt && item.viewedAt >= start && item.viewedAt <= end)
        );
      } else if (status == ProposalStatus.viewed) {
        return item.viewedAt && item.viewedAt >= start && item.viewedAt <= end;
      } else if (status == ProposalStatus.sent) {
        return item.created && item.created >= start && item.created <= end;
      } else if (status == ProposalStatus.accepted) {
        return item.signedAt && item.signedAt >= start && item.signedAt <= end;
      } else if (status == ProposalStatus.archived) {
        return item.created && item.created >= start && item.created <= end;
      } else if (status == ProposalStatus.draft) {
        return item.created && item.created >= start && item.created <= end;
      }
    });
  }, [datesEnabled, formValue.dateRange, items, status]);

  const totals = useMemo(() => {
    return itemsByDateRange.reduce((acc, item) => item.amount + acc, 0);
  }, [itemsByDateRange]);

  if (loading.loading) return <TopLoadingBar />;

  return (
    <div className="page-content">
      <Container fluid>
        <PageHeader
          buttons={[
            {
              children: (
                <div className="d-flex align-items-center">
                  {datesEnabled && (
                    <div key="btn0" style={{ width: 250, marginRight: 10, marginTop: 15 }}>
                      <FormDateRangeExtra control={form.controls.dateRange} />
                    </div>
                  )}
                  <a onClick={() => setDatesEnabled(!datesEnabled)} style={{ cursor: 'pointer' }}>
                    <i
                      className={`bx bx-calendar font-size-16 mr-2 ${classnames({ 'text-muted': !datesEnabled, 'text-primary': datesEnabled })}`}
                    ></i>
                  </a>
                </div>
              ),
            },
            {
              children: (
                <div key="btn1" style={{ width: 205, marginRight: 10, marginTop: 15 }}>
                  <FormInput control={form.controls.search} isClearable />
                </div>
              ),
            },
            {
              children: (
                <div key="btn2" style={{ width: 205, marginRight: 10, marginTop: 15 }}>
                  <FormSelect items={userItems} control={form.controls.createdBy} isClearable />
                </div>
              ),
            },
            // {
            //   title: `Send Proposal`,
            //   // onClick: () => openSelectOrgModal({ onSelect: orgId => history.push('/add-edit-proposal') }),
            //   // onClick: () => history.push('/add-edit-proposal'),
            // },
          ]}
        >
          Proposals
        </PageHeader>
        <div className="main_tabs">
          <TabsHeader
            pills
            tabs={tabs}
            onChange={onTabIndexChange}
            right={
              <div className="text-muted">
                Total: <strong>{Formatter.formatCurrency(totals, company.currencyCode)}</strong>
              </div>
            }
          />
          <CBody>
            <GCTable
              noTableHead
              customStyles={{
                rows: {
                  style: { transition: 'all 0.25s ease-in-out' },
                  highlightOnHoverStyle: { backgroundColor: '#07456B20', cursor: 'pointer', borderRadius: 5 },
                },
              }}
              onRowClicked={row => {
                if (row.status === ProposalStatus.draft) {
                  // openAddProposalModal({ proposalId: row.id, orgId: row.orgId });
                } else {
                  openViewProposal({ proposalId: row.id });
                }
              }}
              highlightOnHover
              pointerOnHover
              noSearch
              striped={false}
              bordered={false}
              noHeader
              sortServer
              columns={columns}
              data={itemsByDateRange}
              paginationRowsPerPageOptions={[25, 50, 100]}
              noQueryOnSearch
            />
          </CBody>
        </div>
      </Container>
    </div>
  );
});
